import React from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import Video from '@components/VideoModule';
import { MEDIA } from '@constants';
import { VideoExtensionType } from '@contracts';
import IconClose from '@images/IconClose';
import Portal from './Portal';

interface Props {
  visible: boolean;
  setVisible: (val: boolean) => void;
  youtubeId?: string;
  videoId?: string;
  width?: number;
  height?: number;
  videoType?: VideoExtensionType;
}

/* 
# FullScreenVideo component accepts props:
# - visible: boolean to control the visibility of the video modal.
# - setVisible: function to update the visibility state.
# - youtubeId: optional string for YouTube video ID.
# - videoId: optional string for video ID.
# - width: optional number for video width.
# - height: optional number for video height.
# - videoType: optional string to specify the video format (H264, mp4, webm). Default is webm. 

!! Important: Video may not play on mobile devices, if videoType is incorrect.
*/
const FullScreenVideo: React.FC<Props> = (props) => {
  const aspectRatio = props.width && props.height ? props.width / props.height : 1.333;

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (props.visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [props.visible]);

  if (!props.visible) return null;

  return (
    <Portal>
      <GrayedOut className="grayed-out" onClick={() => props.setVisible(false)}>
        <LayoutContainer $aspectratio={aspectRatio}>
          <IconClose style={{ cursor: 'pointer' }} onClick={() => props.setVisible(false)} color={Colors.navyBlue} />
          <VideoBackground className="video-frame" onClick={(e) => e.stopPropagation()} $aspectratio={aspectRatio}>
            {props.youtubeId ? (
              <iframe
                className="youtube-video"
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${props.youtubeId}?autoplay=1`}
                allow="autoplay"
                frameBorder="0"
                allowFullScreen
              ></iframe>
            ) : (
              <Video mp4={props.videoId} width={props.width} height={props.height} muted={false} type={props.videoType || VideoExtensionType.WEBM} />
            )}
          </VideoBackground>
        </LayoutContainer>
      </GrayedOut>
    </Portal>
  );
};

export default FullScreenVideo;

const GrayedOut = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 999999992; // we need this to be higher than other modals(999999991) and zendesk iframe(999999)
`;

const LayoutContainer = styled.div<{ $aspectratio: number }>`
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  gap: 16px;
  width: 1400px;
  max-height: 90vh;
  max-width: 80vw;
  aspect-ratio: ${(props) => props.$aspectratio};

  @media (min-width: 1400px) {
    /* for wide screens, always show close icon on the right */
    flex-wrap: nowrap;
  }

  ${MEDIA.tablet} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
  }

  > svg {
    width: 50px;
    height: 50px;
    background-color: ${Colors.cream};
    border-radius: 50%;
    padding: 12px;
    flex-shrink: 0;

    @media (max-width: 700px) {
      width: 40px;
      height: 40px;
      padding: 8px;
    }
  }

  video {
    max-height: 80vh;
  }
`;

const VideoBackground = styled.div<{ $aspectratio: number }>`
  background-color: transparent;
  height: 100%;
  aspect-ratio: ${(props) => props.$aspectratio};
`;
